import { CdkDrag, CdkDragDrop, CdkDropList, DragDropModule } from '@angular/cdk/drag-drop';
import { NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  input,
  output,
  viewChild,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormArray, ReactiveFormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { connectState } from '@examdojo/angular/util';
import { DateTimeModule } from '@examdojo/core/date-time';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { shareOneReplay } from '@examdojo/core/rxjs';
import { SingleSelectComponent } from '@examdojo/core/select';
import { UnwrapSignalValue } from '@examdojo/core/typescript';
import { MarkschemeEditorItemEnum } from '@examdojo/markscheme-v2';
import { ImageContentItemFormGroup } from '@examdojo/models/content-item';
import { MarkCalculationMethod } from '@examdojo/models/markscheme-v2';
import { QuestionItemTypeEnum, QuestionItemV2, SUBSTEM_TAG_VALUES } from '@examdojo/models/question';
import {
  ContentItemFormGroup,
  ContentItemViewerComponent,
  TableContentItemFormGroup,
} from '@examdojo/question-content-item';
import { DifficultyComponent } from '@examdojo/ui/difficulty';
import { SelectorBadgeComponent } from '@examdojo/ui/selector-badge';
import { TranslocoPipe } from '@jsverse/transloco';
import { AngularSplitModule } from 'angular-split';
import { combineLatest, debounceTime, map, startWith, tap } from 'rxjs';
import { calculateQuestionTotalMarks, getDifficultyFromTotalMarks, getMarksFromStem } from '../models/question.model';
import { QuestionItemOptionsTemplateContainerComponent } from './options/question-item-options-template-container.component';
import { QuestionItemOptionsViewerComponent } from './options/question-item-options-viewer.component';
import {
  ActiveQuestionEditorItem,
  ActiveQuestionEditorSubItemCoordinates,
  ActiveQuestionEditorSubItemOptionsCoordinates,
  CONTENT_ITEM_TYPE_TO_COLOR,
  CONTENT_ITEM_TYPE_TO_LONG_LABEL,
  CONTENT_ITEM_TYPE_TO_SHORT_LABEL,
  StemFormGroup,
  StemGroupFormGroup,
  TopLevelFormGroup,
} from './question-item-editor.model';
import { QuestionItemsFormService } from './question-items-form.service';
import { StemLabelIndexDirective } from './stem-label/stem-label-index.directive';
import { StemLabelStateManagerDirective } from './stem-label/stem-label-state-manager.directive';
import { StemLabelComponent } from './stem-label/stem-label.component';
import { QuestionItemEditorStemTemplateContainerComponent } from './stem/question-item-editor-stem-template-container.component';
import { QuestionItemEditorStemViewerComponent } from './stem/question-item-editor-stem-viewer.component';

@Component({
  selector: 'dojo-question-items-editor',
  imports: [
    DateTimeModule,
    ReactiveFormsModule,
    SingleSelectComponent,
    MatTooltipModule,
    MatButtonToggleModule,
    MatExpansionModule,
    DragDropModule,
    MatMenuModule,
    ContentItemViewerComponent,
    AngularSplitModule,
    SelectorBadgeComponent,
    IconButtonComponent,
    TranslocoPipe,
    StemLabelComponent,
    StemLabelStateManagerDirective,
    QuestionItemEditorStemViewerComponent,
    QuestionItemOptionsViewerComponent,
    NgStyle,
    DifficultyComponent,
    QuestionItemEditorStemTemplateContainerComponent,
    StemLabelIndexDirective,
    QuestionItemOptionsTemplateContainerComponent,
  ],
  templateUrl: './question-items-editor.component.html',
  host: { class: 'flex flex-col gap-4' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CdkDropList, CdkDrag],
  styleUrls: ['./question-items-editor.component.scss'],
})
export class QuestionItemsEditorComponent {
  constructor(
    private readonly questionItemsFormService: QuestionItemsFormService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.questionItemsFormService.formStructureChanged$
      .pipe(
        tap(() => this.cdr.detectChanges()),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  readonly mainDropList = viewChild<CdkDropList<QuestionItemV2[]>>('mainDropList');
  readonly subContainers = viewChildren<CdkDropList<QuestionItemV2[]>>('stemGroup');

  readonly QuestionItemTypeEnum = QuestionItemTypeEnum;

  readonly allContainers = computed(() => [...this.subContainers(), this.mainDropList()!]);

  readonly questionItemsForm = this.questionItemsFormService.form;
  readonly activeQuestionSubItem = this.questionItemsFormService.activeQuestionSubItem;
  readonly activeQuestionItem = this.questionItemsFormService.activeQuestionItem;

  readonly isSelectingDisabled = input(false);
  readonly markCalculationMethod = input.required<MarkCalculationMethod>();

  readonly calculatorAllowed = input.required<boolean>();
  readonly allowCalculatorEdit = input<boolean>(false);
  readonly calculatorAllowedChange = output<boolean>();

  readonly stemViewerTemplate =
    input<UnwrapSignalValue<QuestionItemEditorStemTemplateContainerComponent['stemViewerTemplate']>>();

  readonly optionsViewerTemplate =
    input<UnwrapSignalValue<QuestionItemOptionsTemplateContainerComponent['optionsViewerTemplate']>>();

  readonly activeQuestionItemType = this.questionItemsFormService.activeQuestionItemType;

  private readonly totalMarks$ = combineLatest([
    this.questionItemsForm.valueChanges.pipe(
      map(() => this.questionItemsForm.getRawValue() as QuestionItemV2[]),
      startWith(this.questionItemsForm.getRawValue() as QuestionItemV2[]),
    ),
    toObservable(this.markCalculationMethod),
  ]).pipe(
    debounceTime(0),
    map(([questionItems, markCalculationMethod]) => calculateQuestionTotalMarks(questionItems, markCalculationMethod)),
    shareOneReplay(),
  );

  private readonly difficulty$ = this.totalMarks$.pipe(map(getDifficultyFromTotalMarks));

  readonly state = connectState({
    editingOptions: this.questionItemsFormService.editingOptions$,
    totalMarks: this.totalMarks$,
    difficulty: this.difficulty$,
  });

  readonly CONTENT_ITEM_TYPE_TO_COLOR = CONTENT_ITEM_TYPE_TO_COLOR;
  readonly CONTENT_ITEM_TYPE_TO_SHORT_LABEL = CONTENT_ITEM_TYPE_TO_SHORT_LABEL;
  readonly CONTENT_ITEM_TYPE_TO_LONG_LABEL = CONTENT_ITEM_TYPE_TO_LONG_LABEL;

  readonly MarkschemeEditorItemEnum = MarkschemeEditorItemEnum;
  readonly SUBSTEM_TAG_VALUES = SUBSTEM_TAG_VALUES;

  readonly stemGroupEnterPredicate = (item: CdkDrag<TopLevelFormGroup>, _list: CdkDropList<TopLevelFormGroup[]>) => {
    if (item.data.value.type !== QuestionItemTypeEnum.Stem) {
      return false;
    }

    return true;
  };

  getStemTotalMarks(stemFormGroup: StemFormGroup): number {
    const stemValue = stemFormGroup.getRawValue();
    if (!stemValue.markscheme) {
      return 0;
    }
    return getMarksFromStem(stemValue, this.markCalculationMethod());
  }

  setActiveQuestionItem(item: ActiveQuestionEditorItem | null, rootItemIndex: number) {
    this.questionItemsFormService.setActiveQuestionItem(item, {
      rootItemIndex,
    });
  }

  editItem({ item, coordinates }: { item: ContentItemFormGroup; coordinates: ActiveQuestionEditorSubItemCoordinates }) {
    this.questionItemsFormService.setActiveQuestionSubItem(
      item as ImageContentItemFormGroup | TableContentItemFormGroup,
      coordinates,
    );
  }

  editOptionsItem(
    options: TopLevelFormGroup,
    {
      item,
      coordinates,
    }: {
      item: ContentItemFormGroup;
      coordinates: ActiveQuestionEditorSubItemOptionsCoordinates;
    },
    rootItemIndex: number,
  ) {
    if (this.activeQuestionItem()?.ctrl !== options) {
      this.setActiveQuestionItem({ ctrl: options, type: QuestionItemTypeEnum.Options }, rootItemIndex);
    }
    this.editItem({ item, coordinates });
  }

  editStimulusItem(
    stimulus: TopLevelFormGroup,
    {
      item,
      contentItemIndex,
    }: {
      item: ContentItemFormGroup;
      contentItemIndex: number;
    },
    rootItemIndex: number,
  ) {
    // Only set stimulus as active if it's not already active to prevent deselection on repeated clicks
    if (this.activeQuestionItem()?.ctrl !== stimulus) {
      this.setActiveQuestionItem({ ctrl: stimulus, type: QuestionItemTypeEnum.Stimulus }, rootItemIndex);
    }
    this.editItem({ item, coordinates: { content_items: contentItemIndex } });
  }

  editStemGroupItem(
    stemGroup: TopLevelFormGroup,
    {
      item,
      contentItemIndex,
    }: {
      item: ContentItemFormGroup;
      contentItemIndex: number;
    },
    rootItemIndex: number,
  ) {
    // Only set stem group as active if it's not already active to prevent deselection on repeated clicks
    if (this.activeQuestionItem()?.ctrl !== stemGroup) {
      this.setActiveQuestionItem({ ctrl: stemGroup, type: QuestionItemTypeEnum.StemGroup }, rootItemIndex);
    }
    this.editItem({ item, coordinates: { content_items: contentItemIndex } });
  }

  drop(event: CdkDragDrop<TopLevelFormGroup[], TopLevelFormGroup[], TopLevelFormGroup>) {
    if (event.previousContainer === event.container) {
      // Moving within the same main list
      const index = this.questionItemsForm.controls.findIndex((ctrl) => ctrl === event.item.data);
      if (index === -1) {
        console.error('No stem control found');
        return;
      }

      this.questionItemsForm.removeAt(index);
      this.questionItemsForm.insert(event.currentIndex, event.item.data);
    } else {
      const draggedItem = event.item.data;
      if (draggedItem.value.type !== 'stem') {
        return;
      }

      // Moving from stem group to main list
      const sourceStemGroup = this.questionItemsForm.controls.find(
        (ctrl) =>
          ctrl.value.type === 'stem_group' &&
          (ctrl as StemGroupFormGroup).controls.stems.controls.includes(draggedItem as StemFormGroup),
      ) as StemGroupFormGroup | undefined;

      if (!sourceStemGroup) {
        console.error('No stem group control found');
        return;
      }

      const index = sourceStemGroup.controls.stems.controls.findIndex((ctrl) => ctrl === draggedItem);

      if (index === -1) {
        console.error('No stem control found');
        return;
      }

      sourceStemGroup.controls.stems.removeAt(index);

      this.questionItemsForm.insert(event.currentIndex, draggedItem);
    }

    this.questionItemsForm.markAsDirty();
  }

  dropSubStem(
    event: CdkDragDrop<TopLevelFormGroup[], TopLevelFormGroup[], TopLevelFormGroup>,
    targetStemGroup: StemGroupFormGroup,
  ) {
    const droppedItem = event.item.data as StemFormGroup;

    if (droppedItem.value.type !== 'stem') {
      return;
    }

    if (event.previousContainer === event.container) {
      // Moving within the same stem group
      const index = targetStemGroup.controls.stems.controls.findIndex((ctrl) => ctrl === droppedItem);
      if (index === -1) {
        console.error('No stem control found');
        return;
      }

      targetStemGroup.controls.stems.removeAt(index);
      targetStemGroup.controls.stems.insert(event.currentIndex, droppedItem);
    } else {
      // Moving from main list to stem group or from stem group to stem group
      const mainListCtrl = this.questionItemsForm.controls.find(
        (ctrl) => ctrl.value.type === 'stem_group' && ctrl === droppedItem,
      ) as StemGroupFormGroup | undefined;

      const sourceFormArray: FormArray<StemFormGroup> =
        mainListCtrl?.controls.stems ?? (droppedItem.parent as FormArray<StemFormGroup>);
      if (!sourceFormArray) {
        console.error('No source control found');
        return;
      }

      const index = sourceFormArray.controls.findIndex((ctrl) => ctrl === droppedItem);
      if (index === -1) {
        console.error('No stem control found in source');
        return;
      }

      sourceFormArray.removeAt(index, { emitEvent: false });
      targetStemGroup.controls.stems.insert(event.currentIndex, droppedItem);
    }

    this.questionItemsForm.markAsDirty();
  }
}
